import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import ClientAvatar from '../../../components/ClientAvatar';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ConversationList = ({ conversations, selectedConversation, onSelectConversation, searchQuery, onSearchChange }) => {
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const channelOptions = [
    { value: 'all', label: 'All Channels' },
    { value: 'sms', label: 'SMS' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Messages' },
    { value: 'unread', label: 'Unread Only' },
    { value: 'response_required', label: 'Response Required' }
  ];

  const filteredConversations = conversations?.filter(conversation => {
    const matchesSearch = conversation?.client?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         conversation?.lastMessage?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesChannel = filterChannel === 'all' || conversation?.channel === filterChannel;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'unread' && conversation?.unreadCount > 0) ||
                         (filterStatus === 'response_required' && conversation?.responseRequired);
    
    return matchesSearch && matchesChannel && matchesStatus;
  });

  const formatTime = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return messageTime?.toLocaleDateString();
  };

  const getChannelIcon = (channel) => {
    return 'MessageSquare';
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Conversations</h2>
          <Button variant="ghost" size="icon">
            <Icon name="Plus" size={20} />
          </Button>
        </div>
        
        {/* Search */}
        <Input
          type="search"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="mb-3"
        />
        
        {/* Filters */}
        <div className="grid grid-cols-2 gap-2">
          <Select
            options={channelOptions}
            value={filterChannel}
            onChange={setFilterChannel}
            placeholder="Channel"
          />
          <Select
            options={statusOptions}
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder="Status"
          />
        </div>
      </div>
      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No conversations found</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search or filters' : 'Start a conversation with your clients'}
            </p>
          </div>
        ) : (
          filteredConversations?.map((conversation) => (
            <div
              key={conversation?.id}
              onClick={() => onSelectConversation(conversation)}
              className={`flex items-start p-4 border-b border-border cursor-pointer transition-smooth hover:bg-muted ${
                selectedConversation?.id === conversation?.id ? 'bg-accent/10 border-l-4 border-l-primary' : ''
              }`}
            >
              {/* Avatar */}
              <div className="relative mr-3 flex-shrink-0">
                <ClientAvatar
                  src={conversation?.client?.avatar}
                  name={conversation?.client?.name}
                  size={48}
                />
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card flex items-center justify-center bg-primary`}>
                  <Icon name={getChannelIcon(conversation?.channel)} size={8} color="white" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {conversation?.client?.name}
                  </h4>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {conversation?.unreadCount > 0 && (
                      <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                        {conversation?.unreadCount}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatTime(conversation?.lastMessage?.timestamp)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground truncate flex-1 mr-2">
                    {conversation?.lastMessage?.isFromAgent && (
                      <span className="text-primary mr-1">You:</span>
                    )}
                    {conversation?.lastMessage?.content}
                  </p>
                  
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {conversation?.responseRequired && (
                      <Icon name="AlertCircle" size={14} className="text-warning" />
                    )}
                    {conversation?.lastMessage?.isFromAgent && (
                      <Icon 
                        name={conversation?.lastMessage?.status === 'delivered' ? 'Check' : 
                              conversation?.lastMessage?.status === 'read' ? 'CheckCheck' : 'Clock'}
                        size={14}
                        className={conversation?.lastMessage?.status === 'read' ? 'text-primary' : 'text-muted-foreground'}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList;